/**
 * CRA conformity pack — strings.
 *
 * SAME PATTERN AS INCIDENT_STRINGS / CONTENT_WATCH_STRINGS
 *   CRA_STRINGS     English; also the inline `defaultValue` of every lookup, so
 *                   the screen renders correctly before the locale files are
 *                   updated.
 *   CRA_STRINGS_EL  Greek.
 * `registerCraTranslations()` adds both to the running i18n instance without
 * touching i18n/index.ts or the locale JSON, so the parity guard — which reads
 * the JSON — is unaffected. Once merged into en.json and el.json the
 * registration is redundant but harmless: `addResources` never overwrites a key
 * that is already there.
 *
 * THE WORDING THAT MATTERS MOST
 * `cra.notFiled.*` is the product telling the manufacturer, in plain words,
 * that WE DO NOT REPORT ANYTHING TO ENISA on their behalf — the Article 14
 * fields record what they say they did. `cra.legal.*` is the second honest
 * admission: the Declaration of Conformity and the readiness verdict are drafts
 * assembled from the dossier, not legal certification.
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const CRA_STRINGS = {
  // --- Navigation (for whoever adds the Shell entry) ------------------------
  'nav.craConformity': 'CRA conformity',
  'nav.shortCraConformity': 'CRA',

  // --- Register ------------------------------------------------------------
  'cra.title': 'CRA conformity',
  'cra.subtitle':
    'The EU Cyber Resilience Act (Regulation (EU) 2024/2847) conformity pack. Register each product with digital elements, track what it needs for a CE mark, handle vulnerabilities, and generate its EU Declaration of Conformity.',
  'cra.addProduct': 'Add product',
  'cra.loadFailed': 'Could not load the product register.',
  'cra.retry': 'Try again',
  'cra.empty.title': 'No products registered',
  'cra.empty.body':
    'Register a product with digital elements to track its CRA conformity: its class, conformity route, SBOM, support period, vulnerabilities and Declaration of Conformity.',
  'cra.count_one': '{{count}} product',
  'cra.count_other': '{{count}} products',
  'cra.open': 'Open',
  'cra.back': 'Back to products',

  // --- Disclaimers ---------------------------------------------------------
  'cra.notFiled.short': 'OpenComply reports nothing to ENISA.',
  'cra.notFiled.banner':
    'The Article 14 fields below record what you tell us you reported, and when. Reporting an actively-exploited vulnerability or a severe incident to ENISA and your CSIRT is something you do through the official channels.',
  'cra.legal.note':
    'The readiness checklist and the Declaration of Conformity are drafts assembled from the dossier you hold. They are not legal certification and have not been reviewed by qualified counsel.',

  // --- Product fields ------------------------------------------------------
  'cra.field.name': 'Product name',
  'cra.field.version': 'Version / model',
  'cra.field.description': 'Description',
  'cra.field.category': 'Product class',
  'cra.field.conformityRoute': 'Conformity route',
  'cra.field.sbomRef': 'SBOM reference',
  'cra.field.sbomHint': 'A URL or location of the software bill of materials (Annex I Part II(1)).',
  'cra.field.supportUntil': 'Support period until',
  'cra.field.supportHint': 'The date until which security updates are provided (Article 13(8)).',
  'cra.field.status': 'Status',
  'cra.routeAutoHint': 'Leave on the default and we set the route from the class. A critical product cannot self-assess.',

  // --- Categories (CRA Annex III / IV classes) -----------------------------
  'cra.category.DEFAULT': 'Default',
  'cra.category.IMPORTANT_CLASS_I': 'Important — Class I',
  'cra.category.IMPORTANT_CLASS_II': 'Important — Class II',
  'cra.category.CRITICAL': 'Critical',

  // --- Conformity routes ---------------------------------------------------
  'cra.route.SELF_ASSESSMENT': 'Self-assessment',
  'cra.route.THIRD_PARTY': 'Third-party (notified body)',

  // --- Product statuses ----------------------------------------------------
  'cra.status.DRAFT': 'Draft',
  'cra.status.IN_PROGRESS': 'In progress',
  'cra.status.READY': 'Ready',
  'cra.status.CE_MARKED': 'CE marked',

  // --- Create / edit -------------------------------------------------------
  'cra.create.title': 'Add a product',
  'cra.edit.title': 'Edit product',
  'cra.save': 'Save',
  'cra.create.submit': 'Add product',
  'cra.delete': 'Delete product',
  'cra.delete.confirm': 'Delete this product and all of its vulnerability records? This cannot be undone.',

  // --- CE readiness --------------------------------------------------------
  'cra.readiness.title': 'CE readiness',
  'cra.readiness.subtitle': 'What this product needs before the CE mark can be affixed (Annex I, VII, VIII and the Declaration under Annex V).',
  'cra.readiness.READY': 'Ready',
  'cra.readiness.NEARLY': 'Nearly ready',
  'cra.readiness.NOT_READY': 'Not ready',
  'cra.readiness.summary': '{{met}} of {{total}} requirements in place',

  // Checklist item labels (keyed by CeChecklistKey).
  'cra.check.essentialRequirements': 'Essential cybersecurity requirements',
  'cra.check.vulnerabilityHandling': 'Vulnerability handling & coordinated disclosure',
  'cra.check.technicalDocumentation': 'Technical documentation',
  'cra.check.sbom': 'Software bill of materials (SBOM)',
  'cra.check.conformityAssessment': 'Conformity assessment procedure',
  'cra.check.declarationOfConformity': 'EU Declaration of Conformity',
  'cra.check.supportPeriod': 'Support period defined',

  // Item statuses.
  'cra.item.MET': 'In place',
  'cra.item.PARTIAL': 'In progress',
  'cra.item.MISSING': 'Missing',
  'cra.item.NOT_APPLICABLE': 'Not applicable',

  // --- Declaration of Conformity -------------------------------------------
  'cra.declaration.generate': 'Generate Declaration of Conformity',
  'cra.declaration.failed': 'The Declaration could not be produced.',

  // --- Vulnerabilities -----------------------------------------------------
  'cra.vuln.title': 'Vulnerabilities',
  'cra.vuln.subtitle': 'Handled vulnerabilities (Annex I Part II). Mark one actively exploited to track the Article 14 reporting clocks.',
  'cra.vuln.add': 'Add vulnerability',
  'cra.vuln.empty': 'No vulnerabilities recorded for this product.',
  'cra.vuln.count_one': '{{count}} vulnerability',
  'cra.vuln.count_other': '{{count}} vulnerabilities',
  'cra.vuln.field.title': 'Title',
  'cra.vuln.field.identifier': 'Identifier (CVE)',
  'cra.vuln.field.severity': 'Severity',
  'cra.vuln.field.status': 'Status',
  'cra.vuln.field.activelyExploited': 'Actively exploited',
  'cra.vuln.field.activelyExploitedHint': 'Turns on the Article 14 reporting obligation and its 24h / 72h clocks.',
  'cra.vuln.field.reportedToEnisa': 'Reported to ENISA / CSIRT',
  'cra.vuln.field.earlyWarningAt': 'Early warning made at (24h)',
  'cra.vuln.field.notificationAt': 'Notification made at (72h)',
  'cra.vuln.field.finalReportAt': 'Final report made at',
  'cra.vuln.field.remediation': 'Remediation',
  'cra.vuln.create.title': 'Add a vulnerability',
  'cra.vuln.edit.title': 'Edit vulnerability',
  'cra.vuln.delete.confirm': 'Delete this vulnerability record?',

  // Severities & statuses.
  'cra.severity.LOW': 'Low',
  'cra.severity.MEDIUM': 'Medium',
  'cra.severity.HIGH': 'High',
  'cra.severity.CRITICAL': 'Critical',
  'cra.vulnStatus.OPEN': 'Open',
  'cra.vulnStatus.FIXED': 'Fixed',
  'cra.vulnStatus.DISCLOSED': 'Disclosed',

  // --- Article 14 clocks ---------------------------------------------------
  'cra.stage.EARLY_WARNING': 'Early warning',
  'cra.stage.NOTIFICATION': 'Notification',
  'cra.stageLimit.EARLY_WARNING': 'within 24 hours',
  'cra.stageLimit.NOTIFICATION': 'within 72 hours',
  'cra.deadline.open': 'Due {{duration}}',
  'cra.deadline.dueSoon': 'Due soon — {{duration}}',
  'cra.deadline.overdue': 'Overdue by {{duration}}',
  'cra.deadline.onTime': 'Reported on time',
  'cra.deadline.late': 'Reported late',
  'cra.deadline.missed': 'A reporting deadline has passed',

  // --- Errors --------------------------------------------------------------
  'cra.error.CRA_CONFORMITY_ROUTE_NOT_ALLOWED': 'A critical product cannot use the self-assessment route.',
  'cra.error.CRA_PRODUCT_NOT_FOUND': 'That product could not be found.',
} as const;

export type CraKey = keyof typeof CRA_STRINGS;
type CraPluralKey = 'cra.count' | 'cra.vuln.count';

export const CRA_STRINGS_EL: Record<CraKey, string> = {
  'nav.craConformity': 'Συμμόρφωση CRA',
  'nav.shortCraConformity': 'CRA',

  'cra.title': 'Συμμόρφωση CRA',
  'cra.subtitle':
    'Ο φάκελος συμμόρφωσης του Κανονισμού για την Κυβερνοανθεκτικότητα (Κανονισμός (ΕΕ) 2024/2847). Καταχωρίστε κάθε προϊόν με ψηφιακά στοιχεία, παρακολουθήστε τι χρειάζεται για σήμανση CE, διαχειριστείτε ευπάθειες και παραγάγετε τη Δήλωση Συμμόρφωσης ΕΕ.',
  'cra.addProduct': 'Προσθήκη προϊόντος',
  'cra.loadFailed': 'Δεν ήταν δυνατή η φόρτωση του μητρώου προϊόντων.',
  'cra.retry': 'Δοκιμή ξανά',
  'cra.empty.title': 'Δεν έχουν καταχωριστεί προϊόντα',
  'cra.empty.body':
    'Καταχωρίστε ένα προϊόν με ψηφιακά στοιχεία για να παρακολουθείτε τη συμμόρφωσή του με τον CRA: κατηγορία, διαδρομή συμμόρφωσης, SBOM, περίοδο υποστήριξης, ευπάθειες και Δήλωση Συμμόρφωσης.',
  'cra.count_one': '{{count}} προϊόν',
  'cra.count_other': '{{count}} προϊόντα',
  'cra.open': 'Άνοιγμα',
  'cra.back': 'Πίσω στα προϊόντα',

  'cra.notFiled.short': 'Το OpenComply δεν αναφέρει τίποτα στον ENISA.',
  'cra.notFiled.banner':
    'Τα πεδία του Άρθρου 14 παρακάτω καταγράφουν όσα μας δηλώνετε ότι αναφέρατε και πότε. Η αναφορά ενεργά εκμεταλλευόμενης ευπάθειας ή σοβαρού περιστατικού στον ENISA και στο CSIRT σας γίνεται μέσω των επίσημων διαύλων.',
  'cra.legal.note':
    'Η λίστα ετοιμότητας και η Δήλωση Συμμόρφωσης είναι προσχέδια που συγκεντρώνονται από τον φάκελο που τηρείτε. Δεν αποτελούν νομική πιστοποίηση και δεν έχουν ελεγχθεί από εξειδικευμένο νομικό σύμβουλο.',

  'cra.field.name': 'Όνομα προϊόντος',
  'cra.field.version': 'Έκδοση / μοντέλο',
  'cra.field.description': 'Περιγραφή',
  'cra.field.category': 'Κατηγορία προϊόντος',
  'cra.field.conformityRoute': 'Διαδρομή συμμόρφωσης',
  'cra.field.sbomRef': 'Αναφορά SBOM',
  'cra.field.sbomHint': 'Διεύθυνση URL ή τοποθεσία του καταλόγου υλικών λογισμικού (Παράρτημα I Μέρος II(1)).',
  'cra.field.supportUntil': 'Περίοδος υποστήριξης έως',
  'cra.field.supportHint': 'Η ημερομηνία έως την οποία παρέχονται ενημερώσεις ασφαλείας (Άρθρο 13(8)).',
  'cra.field.status': 'Κατάσταση',
  'cra.routeAutoHint': 'Αφήστε την προεπιλογή και ορίζουμε τη διαδρομή από την κατηγορία. Ένα κρίσιμο προϊόν δεν μπορεί να κάνει αυτοαξιολόγηση.',

  'cra.category.DEFAULT': 'Προεπιλογή',
  'cra.category.IMPORTANT_CLASS_I': 'Σημαντικό — Κλάση I',
  'cra.category.IMPORTANT_CLASS_II': 'Σημαντικό — Κλάση II',
  'cra.category.CRITICAL': 'Κρίσιμο',

  'cra.route.SELF_ASSESSMENT': 'Αυτοαξιολόγηση',
  'cra.route.THIRD_PARTY': 'Τρίτος φορέας (κοινοποιημένος οργανισμός)',

  'cra.status.DRAFT': 'Πρόχειρο',
  'cra.status.IN_PROGRESS': 'Σε εξέλιξη',
  'cra.status.READY': 'Έτοιμο',
  'cra.status.CE_MARKED': 'Με σήμανση CE',

  'cra.create.title': 'Προσθήκη προϊόντος',
  'cra.edit.title': 'Επεξεργασία προϊόντος',
  'cra.save': 'Αποθήκευση',
  'cra.create.submit': 'Προσθήκη προϊόντος',
  'cra.delete': 'Διαγραφή προϊόντος',
  'cra.delete.confirm': 'Διαγραφή αυτού του προϊόντος και όλων των αρχείων ευπαθειών του; Η ενέργεια δεν αναιρείται.',

  'cra.readiness.title': 'Ετοιμότητα CE',
  'cra.readiness.subtitle': 'Τι χρειάζεται αυτό το προϊόν πριν τοποθετηθεί η σήμανση CE (Παραρτήματα I, VII, VIII και η Δήλωση κατά το Παράρτημα V).',
  'cra.readiness.READY': 'Έτοιμο',
  'cra.readiness.NEARLY': 'Σχεδόν έτοιμο',
  'cra.readiness.NOT_READY': 'Μη έτοιμο',
  'cra.readiness.summary': '{{met}} από {{total}} απαιτήσεις σε ισχύ',

  'cra.check.essentialRequirements': 'Βασικές απαιτήσεις κυβερνοασφάλειας',
  'cra.check.vulnerabilityHandling': 'Διαχείριση ευπαθειών & συντονισμένη γνωστοποίηση',
  'cra.check.technicalDocumentation': 'Τεχνική τεκμηρίωση',
  'cra.check.sbom': 'Κατάλογος υλικών λογισμικού (SBOM)',
  'cra.check.conformityAssessment': 'Διαδικασία αξιολόγησης συμμόρφωσης',
  'cra.check.declarationOfConformity': 'Δήλωση Συμμόρφωσης ΕΕ',
  'cra.check.supportPeriod': 'Ορισμένη περίοδος υποστήριξης',

  'cra.item.MET': 'Σε ισχύ',
  'cra.item.PARTIAL': 'Σε εξέλιξη',
  'cra.item.MISSING': 'Λείπει',
  'cra.item.NOT_APPLICABLE': 'Μη εφαρμόσιμο',

  'cra.declaration.generate': 'Παραγωγή Δήλωσης Συμμόρφωσης',
  'cra.declaration.failed': 'Δεν ήταν δυνατή η παραγωγή της Δήλωσης.',

  'cra.vuln.title': 'Ευπάθειες',
  'cra.vuln.subtitle': 'Διαχειριζόμενες ευπάθειες (Παράρτημα I Μέρος II). Σημειώστε μία ως ενεργά εκμεταλλευόμενη για να παρακολουθείτε τα χρονόμετρα του Άρθρου 14.',
  'cra.vuln.add': 'Προσθήκη ευπάθειας',
  'cra.vuln.empty': 'Δεν έχουν καταγραφεί ευπάθειες για αυτό το προϊόν.',
  'cra.vuln.count_one': '{{count}} ευπάθεια',
  'cra.vuln.count_other': '{{count}} ευπάθειες',
  'cra.vuln.field.title': 'Τίτλος',
  'cra.vuln.field.identifier': 'Αναγνωριστικό (CVE)',
  'cra.vuln.field.severity': 'Σοβαρότητα',
  'cra.vuln.field.status': 'Κατάσταση',
  'cra.vuln.field.activelyExploited': 'Ενεργά εκμεταλλευόμενη',
  'cra.vuln.field.activelyExploitedHint': 'Ενεργοποιεί την υποχρέωση αναφοράς του Άρθρου 14 και τα χρονόμετρα 24ω / 72ω.',
  'cra.vuln.field.reportedToEnisa': 'Αναφέρθηκε σε ENISA / CSIRT',
  'cra.vuln.field.earlyWarningAt': 'Έγκαιρη προειδοποίηση στις (24ω)',
  'cra.vuln.field.notificationAt': 'Κοινοποίηση στις (72ω)',
  'cra.vuln.field.finalReportAt': 'Τελική αναφορά στις',
  'cra.vuln.field.remediation': 'Αποκατάσταση',
  'cra.vuln.create.title': 'Προσθήκη ευπάθειας',
  'cra.vuln.edit.title': 'Επεξεργασία ευπάθειας',
  'cra.vuln.delete.confirm': 'Διαγραφή αυτού του αρχείου ευπάθειας;',

  'cra.severity.LOW': 'Χαμηλή',
  'cra.severity.MEDIUM': 'Μεσαία',
  'cra.severity.HIGH': 'Υψηλή',
  'cra.severity.CRITICAL': 'Κρίσιμη',
  'cra.vulnStatus.OPEN': 'Ανοικτή',
  'cra.vulnStatus.FIXED': 'Διορθωμένη',
  'cra.vulnStatus.DISCLOSED': 'Γνωστοποιημένη',

  'cra.stage.EARLY_WARNING': 'Έγκαιρη προειδοποίηση',
  'cra.stage.NOTIFICATION': 'Κοινοποίηση',
  'cra.stageLimit.EARLY_WARNING': 'εντός 24 ωρών',
  'cra.stageLimit.NOTIFICATION': 'εντός 72 ωρών',
  'cra.deadline.open': 'Προθεσμία {{duration}}',
  'cra.deadline.dueSoon': 'Λήγει σύντομα — {{duration}}',
  'cra.deadline.overdue': 'Εκπρόθεσμη κατά {{duration}}',
  'cra.deadline.onTime': 'Αναφέρθηκε εμπρόθεσμα',
  'cra.deadline.late': 'Αναφέρθηκε εκπρόθεσμα',
  'cra.deadline.missed': 'Έχει παρέλθει προθεσμία αναφοράς',

  'cra.error.CRA_CONFORMITY_ROUTE_NOT_ALLOWED': 'Ένα κρίσιμο προϊόν δεν μπορεί να χρησιμοποιήσει τη διαδρομή αυτοαξιολόγησης.',
  'cra.error.CRA_PRODUCT_NOT_FOUND': 'Το προϊόν δεν βρέθηκε.',
};

let registered = false;
/**
 * Idempotent, and `addResources` does not overwrite a key the bundled locale
 * already provides, so this is safe to call before and after the PM merges the
 * keys into en.json / el.json.
 */
export function registerCraTranslations() {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [['en', CRA_STRINGS], ['el', CRA_STRINGS_EL]] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerCraTranslations();

export function useCraT() {
  const { t, i18n: instance } = useTranslation();

  const tx = useCallback(
    (key: CraKey, options?: Record<string, unknown>): string =>
      t(key, { defaultValue: CRA_STRINGS[key], ...options }) as string,
    [t],
  );

  const txn = useCallback(
    (key: CraPluralKey, count: number): string =>
      t(key, {
        count,
        defaultValue: CRA_STRINGS[`${key}_${count === 1 ? 'one' : 'other'}` as CraKey],
      }) as string,
    [t],
  );

  const formatDate = useCallback(
    (value: string | Date | null | undefined): string => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(instance.language, { dateStyle: 'medium' }).format(new Date(value));
    },
    [instance.language],
  );

  const formatDateTime = useCallback(
    (value: string | Date | null | undefined): string => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(instance.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
    },
    [instance.language],
  );

  return { tx, txn, formatDate, formatDateTime };
}
